import { ArrowRightIcon, ChevronDownIcon, DownloadIcon, FileTextIcon } from 'core/components/Icons'
import { SamarthLogo } from 'core/components/Logo/SamarthLogo'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

interface SectionItem {
  id: string
  title: string
}

const SECTIONS: SectionItem[] = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'eligibility', title: '2. User Eligibility & Accounts' },
  { id: 'kyc-compliance', title: '3. KYC Verification & Documents' },
  { id: 'orders-payments', title: '4. Indents, Orders & Payments' },
  { id: 'acceptable-use', title: '5. Acceptable Use & Prohibited Acts' },
  { id: 'intellectual-property', title: '6. Intellectual Property Rights' },
  { id: 'disclaimers', title: '7. Disclaimers & Warranties' },
  { id: 'liability', title: '8. Limitation of Liability' },
  { id: 'termination', title: '9. Suspension & Account Termination' },
  { id: 'governing-law', title: '10. Governing Law & Dispute Resolution' },
  { id: 'contact', title: '11. Official Contact Information' },
]

export const TermsOfServicePage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('acceptance')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      if (scrollY + windowHeight >= documentHeight - 60) {
        setActiveSection(SECTIONS[SECTIONS.length - 1].id)
        return
      }

      const threshold = 180
      let currentSection = SECTIONS[0].id

      for (const section of SECTIONS) {
        const el = document.getElementById(section.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= threshold) {
            currentSection = section.id
          }
        }
      }

      setActiveSection(currentSection)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollTo = (id: string) => {
    setActiveSection(id)
    setMobileMenuOpen(false)
    const element = document.getElementById(id)
    if (element) {
      const yOffset = -90
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="min-h-screen bg-bg text-text-primary transition-colors duration-300">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
            <SamarthLogo showText={true} size="small" layout="row" />
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-btn border border-border bg-surface hover:bg-bg transition-colors"
              title="Print or save as PDF"
            >
              <DownloadIcon size={14} />
              Print / Save PDF
            </button>
            <Link
              to="/privacy-policy"
              className="text-xs font-semibold px-3 py-2 rounded-btn text-text-secondary hover:text-text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-btn bg-primary text-primary-fg hover:brightness-110 shadow-soft transition-all"
            >
              Sign In
              <ArrowRightIcon size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-surface border-b border-border py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-3">
            <FileTextIcon size={14} />
            Terms of Service
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-text-primary">
            Terms of Service
          </h1>
          <p className="mt-3 text-base sm:text-lg text-text-secondary max-w-3xl mx-auto">
            Sanjeevani Setu Mobile Application (<code>com.sanjeevani.setu</code>) &amp; Samarth Agri Platform
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-text-secondary">
            <span>
              <strong>Entity:</strong> Samarth EduSkills Pvt. Ltd. (Samarth Agri)
            </span>
            <span>•</span>
            <span>
              <strong>Website:</strong> https://samarthagri.com
            </span>
            <span>•</span>
            <span>
              <strong>Effective Date:</strong> October 2026
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Desktop Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-24">
            <div className="bg-surface rounded-card border border-border p-5 shadow-card">
              <h3 className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-3">Table of Contents</h3>
              <nav className="space-y-1">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-btn text-xs font-semibold transition-all flex items-center justify-between ${
                      activeSection === sec.id
                        ? 'bg-primary text-primary-fg shadow-soft font-bold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg'
                    }`}
                  >
                    <span>{sec.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-6 pt-5 border-t border-border">
                <Link
                  to="/privacy-policy"
                  className="flex items-center justify-between text-xs font-semibold text-primary hover:underline"
                >
                  <span>View Privacy Policy</span>
                  <ArrowRightIcon size={12} />
                </Link>
              </div>
            </div>
          </aside>

          {/* Mobile TOC Accordion */}
          <div className="lg:hidden col-span-1">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between p-4 rounded-card bg-surface border border-border text-sm font-bold text-text-primary"
            >
              <span>Table of Contents</span>
              <ChevronDownIcon
                size={18}
                className={`transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {mobileMenuOpen && (
              <div className="mt-2 p-3 bg-surface rounded-card border border-border shadow-card space-y-1">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-btn text-xs font-semibold transition-all ${
                      activeSection === sec.id
                        ? 'bg-primary text-primary-fg font-bold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg'
                    }`}
                  >
                    {sec.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Policy Text Articles */}
          <main className="lg:col-span-8 bg-surface rounded-card border border-border p-6 sm:p-10 shadow-card space-y-12">
            {/* 1. Acceptance */}
            <section id="acceptance" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                1. Acceptance of Terms
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement between you
                  (&quot;User&quot;, &quot;you&quot;, or &quot;your&quot;) and{' '}
                  <strong className="text-text-primary">Samarth EduSkills Pvt. Ltd. (Samarth Agri)</strong>{' '}
                  (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), governing your access to
                  and use of the <strong className="text-text-primary">Sanjeevani Setu</strong> mobile application
                  (Android package: <code>com.sanjeevani.setu</code>) and related agricultural supply-chain platforms,
                  portals, and services.
                </p>
                <p>
                  By downloading, registering, or using the Application, you acknowledge that you have read, understood,
                  and agree to be bound by these Terms and our companion{' '}
                  <Link to="/privacy-policy" className="text-primary font-semibold hover:underline">
                    Privacy Policy
                  </Link>
                  . If you do not agree to these Terms, you must not use or access the Application.
                </p>
              </div>
            </section>

            {/* 2. Eligibility & Accounts */}
            <section id="eligibility" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                2. User Eligibility &amp; Accounts
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  The Application is intended exclusively for authorized agricultural field coordinators, distributors,
                  retailers, enterprise partners, and farmers operating in lawful commerce within India.
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    You must be at least 18 years of age and competent to enter into a valid contract under Indian law.
                  </li>
                  <li>
                    You are responsible for maintaining the confidentiality of your mobile number, OTP, credentials, and
                    devices.
                  </li>
                  <li>
                    Any activity originating from your registered account shall be deemed to have been executed or
                    authorized by you.
                  </li>
                  <li>
                    You agree to immediately notify us of any unauthorized use or security compromise of your account.
                  </li>
                </ul>
              </div>
            </section>

            {/* 3. KYC Verification */}
            <section id="kyc-compliance" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                3. KYC Verification &amp; Documents
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  To fulfill regulatory guidelines and enable field purchases, wholesale cattle feed indents, and
                  commercial transactions, users must submit Know Your Customer (&quot;KYC&quot;) documentation.
                </p>
                <div className="p-4 rounded-btn bg-bg border border-border/80 text-xs space-y-2">
                  <div className="font-bold text-text-primary">Affirmative Consent &amp; Authenticity</div>
                  <p>
                    By uploading required KYC verification documents (PAN card and GST registration certificate in
                    supported PDF or JPG/PNG image formats), you declare that all documents are genuine, valid, and
                    belong to you or your duly authorized commercial entity.
                  </p>
                </div>
                <p>
                  The Company reserves the right to accept, reject, or request re-verification (Re-KYC) of any account
                  if discrepancies are discovered.
                </p>
              </div>
            </section>

            {/* 4. Indents, Orders & Payments */}
            <section id="orders-payments" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                4. Indents, Orders &amp; Payments
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>The Application enables order placement, feed quantity indents, and invoice fulfillment:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    <strong>Price &amp; Availability:</strong> Cattle feed catalog prices, logistics charges, and
                    dispatch timelines are subject to confirmation at the time of invoice issuance.
                  </li>
                  <li>
                    <strong>Payment Flow:</strong> Digital payments are facilitated via RBI-regulated UPI payment
                    applications (such as Google Pay, PhonePe, Paytm, BHIM) using secure UPI Intent hand-off.
                  </li>
                  <li>
                    <strong>Zero Financial Storage:</strong> Samarth Agri never collects, stores, or handles your bank
                    account passwords, UPI PINs, or debit/credit card security credentials.
                  </li>
                  <li>
                    <strong>Payment Reconciliation:</strong> Payment receipts and bank reference numbers (UTR) provided
                    by the user are validated against bank settlement reports before order dispatch.
                  </li>
                </ul>
              </div>
            </section>

            {/* 5. Acceptable Use */}
            <section id="acceptable-use" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                5. Acceptable Use &amp; Prohibited Acts
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>You agree not to engage in any of the following prohibited behaviors:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Submitting false, forged, or altered identity, tax, or business documentation.</li>
                  <li>Submitting false or fictitious customer records, sales entries, or business addresses.</li>
                  <li>
                    Decompiling, reverse engineering, disassembling, or attempting to derive source code from the
                    Application.
                  </li>
                  <li>
                    Interfering with security mechanisms, rate limiters, or server infrastructures of Samarth Agri.
                  </li>
                  <li>Using the platform for any fraudulent, unauthorized, or unlawful commercial trade.</li>
                </ul>
              </div>
            </section>

            {/* 6. Intellectual Property */}
            <section id="intellectual-property" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                6. Intellectual Property Rights
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  All content, trademarks, logos, brand names, user interface designs, software architectures,
                  algorithms, and documentation relating to Sanjeevani Setu are the exclusive intellectual property of{' '}
                  <strong className="text-text-primary">Samarth EduSkills Pvt. Ltd.</strong> and are protected by
                  applicable intellectual property laws of India.
                </p>
                <p>
                  No right, title, or interest in or to the Application or any content is transferred to you, and all
                  rights not expressly granted are reserved by the Company.
                </p>
              </div>
            </section>

            {/* 7. Disclaimers */}
            <section id="disclaimers" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                7. Disclaimers &amp; Warranties
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  The Application and its contents are provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot;
                  basis without warranties of any kind, either express or implied.
                </p>
                <p>
                  While we strive for high uptime and accurate information, we do not warrant that service will be
                  uninterrupted, error-free, completely immune from cyber threats, or that defects will be instantly
                  corrected.
                </p>
              </div>
            </section>

            {/* 8. Limitation of Liability */}
            <section id="liability" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                8. Limitation of Liability
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  To the maximum extent permitted by applicable Indian law, Samarth EduSkills Pvt. Ltd., its directors,
                  officers, employees, or affiliates shall not be liable for any indirect, incidental, punitive, or
                  consequential damages arising from:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    Your inability to use or access the Application due to network, device, or telecommunications
                    failure.
                  </li>
                  <li>Delays or rejections by banking or third-party UPI payment gateways.</li>
                  <li>
                    Logistics delays caused by force majeure events, severe weather, natural disasters, or
                    transportation strikes.
                  </li>
                </ul>
              </div>
            </section>

            {/* 9. Termination */}
            <section id="termination" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                9. Suspension &amp; Account Termination
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  We reserve the right to suspend or terminate your account and revoke platform access immediately,
                  without prior notice, if:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>You breach any provision of these Terms or companion policies.</li>
                  <li>We suspect fraudulent, deceptive, or unlawful activity on your account.</li>
                  <li>Required by law enforcement or judicial order.</li>
                </ul>
                <p>
                  You may also request account termination and profile data deletion at any time by emailing{' '}
                  <a href="mailto:business@samarthagri.com" className="text-primary font-semibold hover:underline">
                    business@samarthagri.com
                  </a>
                  .
                </p>
              </div>
            </section>

            {/* 10. Governing Law */}
            <section id="governing-law" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                10. Governing Law &amp; Dispute Resolution
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-3">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the Republic of India.
                </p>
                <p>
                  Any dispute, controversy, or claim arising out of or relating to these Terms shall be subject to the
                  exclusive jurisdiction of the competent courts situated in{' '}
                  <strong className="text-text-primary">Gurgaon, Haryana, India</strong>.
                </p>
              </div>
            </section>

            {/* 11. Contact Details */}
            <section id="contact" className="scroll-mt-28">
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary pb-3 border-b border-border">
                11. Official Contact Information
              </h2>
              <div className="mt-4 text-sm text-text-secondary leading-relaxed space-y-4">
                <p>For inquiries, support, or legal communications regarding these Terms:</p>
                <div className="p-5 rounded-btn bg-bg border border-border space-y-2 text-xs">
                  <p className="font-bold text-text-primary text-sm">Samarth EduSkills Pvt. Ltd. (Samarth Agri)</p>
                  <p>
                    <strong>Application:</strong> Sanjeevani Setu (Android: <code>com.sanjeevani.setu</code>)
                  </p>
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:business@samarthagri.com" className="text-primary font-semibold hover:underline">
                      business@samarthagri.com
                    </a>
                  </p>
                  <p>
                    <strong>Phone:</strong> 0124-4165404
                  </p>
                  <p>
                    <strong>Website:</strong>{' '}
                    <a
                      href="https://samarthagri.com"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary font-semibold hover:underline"
                    >
                      https://samarthagri.com
                    </a>
                  </p>
                  <p>
                    <strong>Corporate Address:</strong> A-210, Arcadia, Sector 49 South City, Gurgaon, Haryana - 122018,
                    India
                  </p>
                  <p>
                    <strong>Operating Hours:</strong> Monday – Saturday, 9:30 AM to 6:30 PM IST
                  </p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-surface py-8 px-4 sm:px-6 lg:px-8 text-center text-xs text-text-secondary">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Samarth EduSkills Pvt. Ltd. (Samarth Agri). All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors font-semibold">
              Privacy Policy
            </Link>
            <a
              href="https://samarthagri.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary transition-colors font-semibold"
            >
              samarthagri.com
            </a>
            <Link to="/login" className="hover:text-primary transition-colors font-semibold">
              Admin Portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
